import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Modal, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useSearchParams } from 'react-router-dom';
import HeadTitle from '../../components/HeadTitle';
import Loader from '../../components/common/Loader/Loader';
import UserProfileMainPage from '../../components/user-profile-main/UserProfileMainPage';
import { getActivationCohortMembers } from '../../api/profileActivations';
import { ACTIVE_STATUSES, STATUS_DESCRIPTION, STATUS_LABEL as MEMBER_STATUS_LABEL, STATUS_ORDER } from '../../utils/memberStatus';

/**
 * Cohort Day Detail — a full page (route `/activation-cohort-day`), opened in a new tab
 * when a cell of the Activation Cohort Retention table is clicked. Same pattern as the
 * Daily Actives "Online now" KPI → `/online-now`: it renders inside the admin Layout
 * rather than as a modal, so it is linkable and survives a reload.
 *
 * The mockup built this page with `window.document.write(...)` into a blank tab and
 * filled it with member names hashed out of a seed — its own footer said "Demo data …
 * wire the real activity feed to replace them". This is that real feed: every person
 * listed is a real member returned by GET /stat/getActivationCohortMembers, which reads
 * the same cohort window as the table, so the counts here always match the cell clicked.
 *
 * "Active" on this page is the admin-wide ACTIVE total — Approved + Hidden — since
 * 2026-09-24. It was approved-only from the v3 rework until then, which made this the one
 * screen where "active" meant something narrower than everywhere else. "Went inactive" is
 * the server's movement name (left the active set) — NOT the Inactive status — so the page
 * shows it as "left the active set" and the two cannot be read as one thing.
 *
 * The design's four tiles and two member cards, exactly. "Went inactive" is the REAL set
 * of members who left the approved set (computed server-side), not the mockup's derived
 * `prev + newlyActivated − cur` — that formula assumes nobody ever returns and so
 * under-reports the drop on real data, where members come back constantly.
 */
const STYLES = `
.favor-acd{
  --black:#0c0c0e;--gold:#c9a35e;--card:#ffffff;--line:#e9e5df;--line-strong:#ddd6cc;
  --txt:#1c1b1a;--txt-2:#6d6a64;--txt-3:#9b978f;
  --green:#1f9d6b;--green-soft:#e7f6ef;--blue:#2f7df6;--blue-soft:#eef4fe;
  --red:#cf4233;--red-soft:#fdecea;--rose:#b03a63;--rose-soft:#fdeef3;
  --amber:#b07d18;--amber-soft:#fdf3e0;
  --serif:"ivypresto-headline",Georgia,serif;
  --sans:"indivisible","Neue Montreal","Helvetica Neue",Arial,sans-serif;
  color:var(--txt);font-family:var(--sans);font-size:14px;line-height:1.5;-webkit-font-smoothing:antialiased;
}
.favor-acd *{margin:0;padding:0;box-sizing:border-box}
.favor-acd .crumb{font-size:12px;color:var(--txt-3);margin-bottom:10px}
.favor-acd .chips{display:flex;gap:7px;margin:12px 0 20px;flex-wrap:wrap}
.favor-acd .chp{font-size:10.5px;font-weight:700;letter-spacing:.4px;padding:4px 11px;border-radius:999px;background:#f1efe9;color:var(--txt-2)}
.favor-acd .chp.b{background:var(--blue-soft);color:var(--blue)}
.favor-acd .stats{display:grid;grid-template-columns:repeat(auto-fit,minmax(165px,1fr));gap:13px;margin-bottom:20px}
.favor-acd .st{background:var(--card);border:1px solid var(--line);border-radius:13px;padding:14px 16px;position:relative;overflow:hidden}
.favor-acd .st .br{position:absolute;left:0;top:0;bottom:0;width:4px}
.favor-acd .st .k{font-size:10.5px;letter-spacing:1.1px;text-transform:uppercase;color:var(--txt-3);font-weight:600}
.favor-acd .st .v{font-family:var(--serif);font-size:22px;font-weight:700;line-height:1;margin-top:8px}
.favor-acd .st .d{font-size:11.5px;color:var(--txt-2);margin-top:4px}
.favor-acd .gz{color:var(--green)} .favor-acd .rz{color:var(--red)} .favor-acd .bz{color:var(--blue)}
.favor-acd .panel{background:var(--card);border:1px solid var(--line);border-radius:13px;padding:16px 18px;margin-bottom:20px}
.favor-acd .panel .k{font-size:10.5px;letter-spacing:1.3px;text-transform:uppercase;color:var(--txt-3);font-weight:700}
.favor-acd .spark{display:flex;gap:3px;align-items:flex-end;margin-top:26px;overflow-x:auto;padding-bottom:2px}
.favor-acd .sb{background:#dcd7ce;border-radius:3px 3px 1px 1px;flex:none;position:relative}
.favor-acd .sb.on{background:var(--blue);outline:2px solid var(--gold);outline-offset:2px}
.favor-acd .sb i{position:absolute;top:-15px;left:-10px;right:-10px;text-align:center;font-style:normal;font-size:9px;font-weight:700;color:var(--txt-2)}
.favor-acd .sb.on i{color:var(--blue)}
.favor-acd .cols{display:flex;gap:18px;flex-wrap:wrap;align-items:flex-start}
.favor-acd .col{flex:1;min-width:290px;background:var(--card);border:1px solid var(--line);border-radius:13px;overflow:hidden}
/* A lone card keeps the width it would have had in a pair, rather than stretching across
   the page. This happens routinely now that empty cards are dropped — Day 0 has only
   ACTIVE THIS DAY — and a single full-bleed card of names reads as a much bigger result
   than it is. calc(50% - 9px) is exactly half the row minus half the 18px gap, so it lines
   up with the left card of any two-card day. Held back until 700px: below that, half a row
   is under the 290px min-width and the card would fight its own minimum. */
@media(min-width:700px){
  .favor-acd .cols .col:only-child{flex:0 1 calc(50% - 9px);max-width:calc(50% - 9px)}
}
.favor-acd .ch{display:flex;justify-content:space-between;align-items:center;font-size:11px;letter-spacing:1.4px;font-weight:800;padding:12px 16px}
.favor-acd .ch.cur{background:var(--blue-soft);color:var(--blue)}
.favor-acd .ch.add{background:var(--green-soft);color:var(--green)}
.favor-acd .ch.ret{background:var(--blue-soft);color:var(--blue)}
.favor-acd .ch.drp{background:var(--red-soft);color:var(--red)}
.favor-acd .cb{font-family:var(--serif);font-size:16px;letter-spacing:0}
.favor-acd .colh{display:flex;justify-content:space-between;gap:8px;padding:7px 16px;border-bottom:1px solid var(--line);font-size:9.5px;letter-spacing:.9px;text-transform:uppercase;color:var(--txt-3);font-weight:700}
.favor-acd .colh .ch-r{text-align:right}
.favor-acd .pr{display:flex;align-items:center;gap:8px;padding:9px 16px;border-bottom:1px solid #f0ede8;font-size:13.5px;cursor:pointer;text-align:left;width:100%}
.favor-acd .pr:last-child{border-bottom:0}
.favor-acd .pr:hover{background:#faf8f5}
.favor-acd .pr:focus-visible{outline:2px solid var(--blue);outline-offset:-2px}
.favor-acd .pa{width:32px;height:32px;border-radius:9px;flex:none;overflow:hidden;background:#e9e5df;display:block;margin-right:3px}
.favor-acd .pa img{width:100%;height:100%;object-fit:cover;display:block}
/* min-width:0 + ellipsis so a long name shrinks instead of shoving the chips out of the card */
.favor-acd .pn{font-weight:600;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.favor-acd .pn small{display:block;font-weight:500;font-size:11px;color:var(--txt-3);white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
/* Extra separation from the status chip: at the row's uniform 8px gap the two pills
   read as one blob ("SUSPENDED WOMAN"), because they are the same shape and size. */
.favor-acd .pg{font-size:9.5px;font-weight:800;letter-spacing:.4px;border-radius:999px;padding:3px 8px;flex:none;margin-left:10px}
.favor-acd .pg.gm{background:var(--blue-soft);color:var(--blue)}
.favor-acd .pg.gf{background:var(--rose-soft);color:var(--rose)}
/* Current account status. margin-left:auto groups this and the gender pill to the right. */
.favor-acd .ps{margin-left:auto;font-size:9.5px;font-weight:800;letter-spacing:.4px;border-radius:999px;padding:3px 8px;flex:none;background:#f1efe9;color:var(--txt-2);white-space:nowrap}
.favor-acd .ps.s-approved,.favor-acd .ps.s-active{background:var(--green-soft);color:var(--green)}
.favor-acd .ps.s-inactive{background:#f1efe9;color:var(--red)}
.favor-acd .ps.s-ghost{background:#f1efe9;color:var(--txt-2)}
.favor-acd .ps.s-hidden{background:var(--amber-soft);color:var(--amber)}
.favor-acd .ps.s-suspended{background:var(--red-soft);color:var(--red)}
.favor-acd .ps.s-deactivated{background:var(--red-soft);color:var(--red)}
.favor-acd .ps.s-deleted{background:#2b2b2b;color:#fff}
.favor-acd .why{padding:16px;color:var(--txt-3);font-size:12.5px}
.favor-acd .ft{margin-top:22px;font-size:11.5px;color:var(--txt-3);line-height:1.6}
`;

// Deterministic placeholder tint for members with no photo — keyed off the member id so a
// given person always gets the same colour.
const PALETTE = [
  'linear-gradient(150deg,#33418f,#1e2a66)', 'linear-gradient(150deg,#96613d,#6e4426)',
  'linear-gradient(150deg,#6f4da0,#4d3374)', 'linear-gradient(150deg,#3f7d5f,#2c5a43)',
  'linear-gradient(150deg,#8f3f5f,#672a44)', 'linear-gradient(150deg,#3f6f8f,#2b4f68)',
  'linear-gradient(150deg,#8f6a2f,#67491d)', 'linear-gradient(150deg,#5f5f8f,#414166)',
];
const tintFor = (id) => {
  let h = 0;
  for (let i = 0; i < id.length; i += 1) { h = (h * 31 + id.charCodeAt(i)) >>> 0; }
  return PALETTE[h % PALETTE.length];
};

/**
 * `acct` is the member's CURRENT canonical bucket, read live — not their status on the
 * cohort day. Those routinely differ: a cohort is fixed on its day 0 and never filtered
 * by status, so someone who activated in May and was suspended in July still sits in
 * their May cohort, and shows SUSPENDED here. There is no status history to read, so
 * "as of now" is the only honest thing this column can say.
 */
// Canonical status order (utils/memberStatus.js). Drives the LEFT APPROVED card split
// so the cards always appear in the same sequence regardless of which buckets are present.
// `unknown` is not a canonical bucket — the server banks members here when they vanish
// from `users` between building the cohort and reading their status (i.e. they hard-deleted
// mid-request). It exists so the cards always sum to the drop total instead of quietly
// falling short. Last in the order because it is a remainder, not a state.
// `approved` can appear too: a member who left the approved set on this day and has since
// come back is approved today.
const CANON_ORDER = [...STATUS_ORDER, 'unknown'];

// DISPLAY buckets for the LEFT APPROVED split. Approved and Hidden are shown as the single
// ACTIVE total the rest of the admin reports (Active = Approved + Hidden), because the
// distinction between "came back visible" and "came back hidden" is not what anyone acts on
// here — both mean the member is still an active member. Every other bucket stands alone, so
// the cards still partition the drop exactly and still sum to counts.wentInactive.
// The per-member chips are NOT merged: each one keeps rendering its own `acct`, so an ACTIVE
// card lists members reading APPROVED and HIDDEN individually.
const DROP_ORDER = ['active', ...CANON_ORDER.filter((s) => !ACTIVE_STATUSES.includes(s))];
// The raw `acct` values each display bucket collects. Anything not listed collects itself.
const DROP_MEMBERS = { active: ACTIVE_STATUSES };
const dropStatuses = (s) => DROP_MEMBERS[s] || [s];
// Total from the SERVER's per-status counts, never from the capped array — same rule as the
// unmerged cards, just summed across the statuses this display bucket covers.
const dropTotal = (byStatus, s) => dropStatuses(s).reduce((n, k) => n + (byStatus[k] || 0), 0);

// One label + colour per bucket, used by BOTH the top tiles and the member cards so the
// two never drift apart. Colours match the `.ps.s-*` status pills below, so a tile and
// the chips inside its card read as the same thing.
const STATUS_LABEL = { ...MEMBER_STATUS_LABEL, unknown: 'Unknown' };
const STATUS_COLOR = {
  // `active` is the merged Approved + Hidden card; it takes Approved's green because the
  // reading is the same — these members are still active members.
  active: 'var(--green)',
  approved: 'var(--green)', hidden: 'var(--amber)', inactive: 'var(--red)', ghost: 'var(--txt-2)',
  deactivated: 'var(--red)', suspended: 'var(--red)', deleted: '#2b2b2b',
  unknown: 'var(--txt-3)',
};

const STATUS_TITLE = Object.fromEntries(
  STATUS_ORDER.map((k) => [k, `${MEMBER_STATUS_LABEL[k]} now — ${STATUS_DESCRIPTION[k]}`]),
);

/**
 * Deletion dates come off the wire as UTC ISO strings, but every date on this tab (cohort
 * days, dayIso, the grid) is an IST calendar day. `String(v).slice(0, 10)` therefore read a
 * day EARLY for anyone who deleted between 00:00 and 05:30 IST — measured 2026-07-20: six
 * members who signed up, activated and deleted on 20 Jul (00:17–03:52 IST) were all labelled
 * "deleted 2026-07-19", i.e. the day before the cohort they were sitting in. Nothing was
 * wrong with the data — zero of the 2,880 delete-log rows have a deletion before its signup.
 */
const IST_DAY_FMT = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Asia/Kolkata', year: 'numeric', month: '2-digit', day: '2-digit',
});
const istDay = (v) => {
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : IST_DAY_FMT.format(d);
};

const PersonRow = ({ u, onOpen }) => (
  <div
    className="pr"
    role="button"
    tabIndex={0}
    title="Open profile"
    onClick={() => onOpen({ id: u.id, deleted: !!u.deleted })}
    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen({ id: u.id, deleted: !!u.deleted }); } }}
  >
    <span className="pa" style={u.profilePicture ? undefined : { background: tintFor(u.id) }}>
      {u.profilePicture && (
        <img src={u.profilePicture} alt="" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
      )}
    </span>
    <span className="pn">
      {u.name || '—'}{u.age ? `, ${u.age}` : ''}
      {/* Signup date is shown whenever it differs from the activation date. A cohort is
          dated by the day the profile was COMPLETED, which for ~2.4% of members is well
          after the day they signed up — one measured case activated 97 days late. Without
          this line a legitimate late activator is indistinguishable from an old account
          wrongly pulled into the day, which is exactly how this got reported as a bug.
          The API has always returned joinedAt/activatedAt; the page just discarded them. */}
      <small>{[
        u.userName ? `@${u.userName}` : null,
        u.city,
        u.joinedAt && istDay(u.joinedAt) !== istDay(u.activatedAt) ? `joined ${istDay(u.joinedAt)}` : null,
        u.deleted && u.deletedAt ? `deleted ${istDay(u.deletedAt)}` : null,
      ].filter(Boolean).join(' · ')}</small>
    </span>
    <span
      className={`ps s-${u.acct || 'ghost'}`}
      title={STATUS_TITLE[u.acct || 'ghost'] || 'Current account status'}
    >
      {/* A missing status falls back to ghost — the same default the server's
          wentInactiveByStatus uses — so the chip agrees with the card it sits in. */}
      {(STATUS_LABEL[u.acct || 'ghost'] || u.acct).toUpperCase()}
    </span>
    <span className={`pg ${u.g === 'm' ? 'gm' : 'gf'}`}>{u.g === 'm' ? 'MAN' : 'WOMAN'}</span>
  </div>
);

const ListCard = ({ kind, title, arr, total, capped, cap, emptyWhy, onOpen }) => (
  <div className="col">
    <div className={`ch ${kind}`}>
      {title}
      <span className="cb">{kind === 'cur' ? total.toLocaleString() : `${kind === 'drp' ? '−' : '+'}${total}`}</span>
    </div>
    {arr.length === 0
      ? <div className="why">{emptyWhy}</div>
      : (
        <>
          <div className="colh"><span>Member</span><span className="ch-r">Status now &middot; Gender</span></div>
          {arr.map((u) => <PersonRow key={u.id} u={u} onOpen={onOpen} />)}
          {/* total − arr.length, not total − cap: the LEFT APPROVED cards are filtered
              client-side out of one shared capped page, so each card shows a different
              number of rows and `cap` is not its page size. For the unsplit cards
              arr.length === cap whenever capped, so this is identical there. */}
          {capped && <div className="why">+ {total - arr.length} more not shown</div>}
        </>
      )}
  </div>
);

const ActivationCohortDay = () => {
  const [params] = useSearchParams();
  const basis = params.get('basis') === 'sign' ? 'sign' : 'act';
  const gender = ['o', 'm', 'f'].includes(params.get('gender')) ? params.get('gender') : 'o';
  const iso = params.get('iso') || '';
  const day = Number.parseInt(params.get('day'), 10) || 0;

  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  // { id, deleted } — a deleted member's id is an account_delete_logs _id, not a user
  // id, so the modal has to be told which collection to read.
  const [profile, setProfile] = useState(null);

  const load = useCallback(async () => {
    try {
      setError(null);
      const d = await getActivationCohortMembers({ basis, gender, iso, day });
      if (!d) throw new Error('empty response');
      setData(d);
    } catch (err) {
      setError(err.message || 'request failed');
    }
  }, [basis, gender, iso, day]);

  useEffect(() => { load(); }, [load]);

  const spark = useMemo(() => {
    if (!data || !data.traj || !data.traj.length) return null;
    const hmax = Math.max(1, ...data.traj);
    const many = data.traj.length > 32;
    const bw = many ? 9 : 16;
    return data.traj.map((v, i) => {
      const h = Math.max(3, Math.round((v / hmax) * 64));
      const on = i === day;
      const showLab = on || i === 0 || i === data.traj.length - 1;
      return (
        <span
          key={i}
          className={`sb${on ? ' on' : ''}`}
          style={{ height: h, width: bw }}
          title={`Day ${i}: ${v} approved`}
        >
          {showLab && <i>{v}</i>}
        </span>
      );
    });
  }, [data, day]);

  const sgn = basis === 'sign';
  // The members behind the clicked cell. The design lists only the movements, so the
  // standing population had nowhere to be seen — ACTIVE THIS DAY is that. It is
  // deliberately NOT called "newly activated": those people activated on the cohort's own
  // Day 0, and counting them as activations again on every day they stay approved would
  // contradict the bars at the top of the tab.
  //
  // Only NEWLY ACTIVATED is withheld when empty (user's call). On an activation cohort it
  // is 0 on every day but Day 0 BY DEFINITION — the cohort is fixed on its first day — so
  // an empty card there is pure noise. A zero on the other two is a real reading and stays
  // on screen.
  //
  // It is ALSO withheld on Day 0, where it is a guaranteed byte-for-byte DUPLICATE of
  // ACTIVE THIS DAY rather than a second reading. Day 0 has no previous day, so the server's
  // `prevSet` is empty, every member of `inNow` lands in `added`, and `newlyActivated =
  // added.filter(p => p.ai === o)` then matches all of them on BOTH bases (sign: inNow is
  // already `ai === anchor`; act: the cohort is bucketed by `ai`). Reported 2026-07-20 on
  // the 20 Jul signup cohort — the same 81 people, in the same order, listed twice.
  const dupeOnDay0 = day === 0;
  const cards = !data ? [] : [
    {
      kind: 'cur',
      // On Day 0 this list IS the activations — say so, since the separate NEWLY ACTIVATED
      // card is suppressed as a duplicate.
      //
      // Off Day 0 this shows `continuingActive`, NOT the full approved set: the two used to
      // overlap, because inNow contains the newly-activated, so the same members were
      // listed on both cards. The server now sends the disjoint list.
      //
      // The title says CONTINUING because the number no longer equals the grid cell that
      // was clicked — cell = continuing + newly. Leaving it labelled "ACTIVE THIS DAY"
      // while showing 89 against a cell reading 94 is exactly the silent disagreement
      // this page was built to avoid; the tiles above still report the full `cur`.
      // "· CONTINUING" only when the split actually removes someone — with no newly
      // activated the list IS the full approved set and the qualifier would be noise.
      title: dupeOnDay0
        ? 'ACTIVE THIS DAY · ALL NEWLY ACTIVATED'
        : (data.counts.newlyActivated > 0 ? 'ACTIVE THIS DAY · CONTINUING' : 'ACTIVE THIS DAY'),
      arr: (dupeOnDay0 ? data.activeNow : data.continuingActive) || [],
      total: dupeOnDay0 ? data.cur : (data.counts.continuingActive ?? data.cur),
      capped: dupeOnDay0 ? data.capped.activeNow : data.capped.continuingActive,
      emptyWhy: dupeOnDay0
        ? 'Nobody from this cohort was approved on this day.'
        : 'Everyone approved on this day activated today — see NEWLY ACTIVATED.',
    },
    {
      kind: 'add',
      title: 'NEWLY ACTIVATED',
      arr: data.newlyActivated,
      total: data.counts.newlyActivated,
      capped: data.capped.newlyActivated,
      hideWhenEmpty: true,
      hideAsDuplicate: dupeOnDay0,
    },
    // WENT INACTIVE (left approved), split one card per CURRENT account status instead of
    // one lump — with Approved and Hidden merged into one ACTIVE card (see DROP_ORDER).
    //
    // The single card answered "how many left" but not "how", which is the part anyone
    // acts on — a cohort losing 30 to DELETED is a different problem from one losing 30
    // to INACTIVE. Splitting also gives the one-member-one-card guarantee: `acct` is a
    // single canonical bucket, so these cards partition the drop exactly and their
    // totals sum to counts.wentInactive.
    //
    // Totals come from the SERVER's wentInactiveByStatus, never from counting the array
    // — the array is capped at `cap` (200), so on a large cohort counting it would
    // understate every bucket while looking authoritative. The member list is filtered
    // client-side from that same capped array, so `capped` is per-card: true when this
    // status has more members than the page actually carries.
    ...(() => {
      const byStatus = data.wentInactiveByStatus || {};
      // byStatus sums to counts.wentInactive, so an empty `present` means nobody dropped
      // — no fallback card needed, the zero-filter below would remove it anyway.
      const present = DROP_ORDER.filter((s) => dropTotal(byStatus, s) > 0);
      return present.map((s) => {
        const want = dropStatuses(s);
        const arr = (data.wentInactive || []).filter((u) => want.includes(u.acct || 'ghost'));
        const total = dropTotal(byStatus, s);
        return {
          kind: 'drp',
          // Short status label, matching the tile above it. The `drp` kind renders the
          // count with a leading "−", which is what carries "these people LEFT the approved
          // set today" now that the WENT INACTIVE prefix is gone.
          title: STATUS_LABEL[s].toUpperCase(),
          arr,
          total,
          capped: arr.length < total,
          // `unknown` never has rows by construction — the members are gone from `users`,
          // which is exactly why they landed here. Every other status reaches this only
          // when the drop exceeds `cap` and all of its members sit past the cut.
          emptyWhy: s === 'unknown'
            ? `${total} member${total === 1 ? '' : 's'} deleted their account while this page was loading, so their last status can no longer be read.`
            : `All ${total} are beyond the first ${data.cap} members shown.`,
        };
      });
    })(),
  ]
    // Drop every empty card, not just the ones flagged: a card reading 0 is noise on a
    // page whose whole job is showing WHO is in a bucket. If they all come out empty the
    // page falls through to its own "nothing happened" message below.
    .filter((c) => !c.hideAsDuplicate && c.total > 0);

  // Tiles: the same buckets as the cards, in the same order (DROP_ORDER), zeros omitted.
  const tiles = !data ? [] : (() => {
    const byStatus = data.wentInactiveByStatus || {};
    const approvedTotal = dupeOnDay0 ? data.cur : (data.counts.continuingActive ?? data.cur);
    const out = [];
    if (approvedTotal > 0) {
      out.push({
        k: dupeOnDay0 ? 'Active this day' : (data.counts.newlyActivated > 0 ? 'Active this day · continuing' : 'Active this day'),
        v: approvedTotal.toLocaleString(),
        c: 'var(--blue)',
        d: data.base ? `${Math.round((100 * approvedTotal) / data.base)}% of the cohort` : '—',
      });
    }
    if (!dupeOnDay0 && data.counts.newlyActivated > 0) {
      out.push({
        k: 'Newly activated',
        v: `+${data.counts.newlyActivated.toLocaleString()}`,
        c: 'var(--green)',
        cls: 'gz',
        d: sgn ? 'late profile completions' : 'not possible after Day 0',
      });
    }
    // Drops, one tile per canonical status. The caption is what carries "these left the
    // approved set" now that the tile label is just the status name.
    DROP_ORDER.filter((s) => dropTotal(byStatus, s) > 0).forEach((s) => {
      out.push({
        k: STATUS_LABEL[s],
        v: `−${dropTotal(byStatus, s).toLocaleString()}`,
        c: STATUS_COLOR[s],
        cls: 'rz',
        // The Active tile covers two buckets, so say which — without it a reader cannot tell
        // whether a member who left approved came back visible or came back hidden.
        d: s === 'active'
          ? (day > 0 ? `approved + hidden · left approved since Day ${day - 1}` : 'approved + hidden · left approved')
          : (day > 0 ? `left approved since Day ${day - 1}` : 'left approved'),
      });
    });
    return out;
  })();
  const gtxt = { o: 'Overall', m: 'Men only', f: 'Women only' }[gender];

  return (
    <Box sx={{ pb: 4 }}>
      <style>{STYLES}</style>
      <HeadTitle
        imageSrc="/assets/images/admin-panel/analytics.png"
        text="Cohort Day Detail"
        description="Who activated, came back and left approved on one day of one cohort."
      />
      <div className="favor-acd">
        {error && <div className="panel" style={{ color: 'var(--red)' }}>Couldn’t load this cohort day: {error}</div>}
        {!data && !error && (
          <div style={{ minHeight: 'calc(100vh - 240px)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Loader />
          </div>
        )}
        {data && (
          <>
            <div className="crumb">Profile Activations &rsaquo; App Activity Retention &rsaquo; <b>Day detail</b></div>
            <div className="chips">
              <span className="chp b">{gtxt.toUpperCase()}</span>
              <span className="chp">{sgn ? 'CAMPAIGN (SIGNUP) BASIS' : 'ACTIVATION BASIS'}</span>
              <span className="chp">{`BASE: ${data.base.toLocaleString()}${sgn ? ' SIGNED UP' : ' ACTIVATED'}`}</span>
              <span className="chp">{`${data.lab} · DAY ${day} · ${data.dayIso}`}</span>
            </div>

            {/* Tiles mirror the member cards below, one for one: same labels, same
                numbers, same order. Each member counts toward exactly one tile
                (approved-this-day excludes the newly activated; the drop tiles are a
                canonical-bucket partition), so the row reads as a breakdown rather than
                a set of overlapping headlines. Zero-valued tiles are dropped entirely —
                a wall of "0" told the reader nothing and pushed the real numbers off. */}
            <div className="stats">
              {tiles.map((t) => (
                <div className="st" key={t.k}>
                  <span className="br" style={{ background: t.c }} />
                  <div className="k">{t.k}</div>
                  <div className={`v ${t.cls || ''}`}>{t.v}</div>
                  <div className="d">{t.d}</div>
                </div>
              ))}
            </div>

            <div className="panel">
              <span className="k">{`Cohort trajectory · approved by day — Day ${day} highlighted`}</span>
              <div className="spark">{spark}</div>
            </div>

            {/* Only cards that actually have members. An empty card is pure noise: a
                "+0 / No activations recorded this day" panel eats a third of the row to say
                nothing, and on an activation cohort it is 0 on every day but Day 0 BY
                DEFINITION. The tiles above already report every count including the zeros,
                so hiding the empty lists loses nothing. */}
            {cards.length > 0 && (
              <div className="cols">
                {cards.map((c) => (
                  <ListCard
                    key={c.kind}
                    kind={c.kind}
                    title={c.title}
                    arr={c.arr}
                    total={c.total}
                    capped={c.capped}
                    cap={data.cap}
                    emptyWhy={c.emptyWhy}
                    onOpen={setProfile}
                  />
                ))}
              </div>
            )}
            {cards.length === 0 && (
              <div className="panel"><span className="k">No members to list for this day</span></div>
            )}

            <div className="ft">
              <b>Status now</b> is each member&rsquo;s account state <i>today</i>, not on {data.dayIso} — a cohort is
              fixed on its Day&nbsp;0 and never filtered by status, so a member who activated here and was
              hidden later still belongs to this cohort and shows HIDDEN.
              {sgn && ` Of ${data.base.toLocaleString()} who signed up, ${data.na.toLocaleString()} activated on this specific day.`}
              <br />
              Click any member to open their profile.
              <br />
              Generated from the Profile Activations report &middot; Favor Admin Console.
            </div>
          </>
        )}

        {/* Same nested-profile pattern as the Online Right Now roster. */}
        <Modal open={!!profile} onClose={() => setProfile(null)}>
          <Box sx={profileModalStyle}>
            <Box sx={{ position: 'relative', height: 44 }}>
              <IconButton sx={{ position: 'absolute', top: 10, right: 0 }} onClick={() => setProfile(null)}>
                <CloseIcon />
              </IconButton>
            </Box>
            {profile && <UserProfileMainPage userId={profile.id} isDeletedAccount={profile.deleted} />}
          </Box>
        </Modal>
      </div>
    </Box>
  );
};

const profileModalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  maxWidth: 1300,
  width: '95%',
  bgcolor: 'background.paper',
  borderRadius: '20px',
  boxShadow: 24,
  p: '0 30px 30px 30px',
  maxHeight: '80vh',
  overflow: 'auto',
};

export default ActivationCohortDay;
